import { useState, useCallback } from 'react';
import {
  joinLocalPath,
  localParent,
  joinRemotePath,
  remoteParent } from
'../utils/paths';
import { normalizeListingResult } from '../utils/listings';

export const useFileOperations = (api, settings, addLog) => {
  const [localPath, setLocalPath] = useState('');
  const [remotePath, setRemotePath] = useState('/');
  const [localEntries, setLocalEntries] = useState([]);
  const [remoteEntries, setRemoteEntries] = useState([]);
  const [selectedLocal, setSelectedLocal] = useState(null);
  const [selectedRemote, setSelectedRemote] = useState(null);
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [loadingRemotePath, setLoadingRemotePath] = useState(null);
  const [loadingLocal, setLoadingLocal] = useState(false);


  const [localSelectedSet, setLocalSelectedSet] = useState(new Set());
  const [remoteSelectedSet, setRemoteSelectedSet] = useState(new Set());

  const createDuplicateNameError = (name) =>
    new Error(`A file or folder named "${name}" already exists.`);

  const refreshLocal = useCallback(
    async (target = localPath) => {
      if (!api) return;
      setLoadingLocal(true);
      try {
        const list = await api.listLocal(target);
        setLocalEntries(Array.isArray(list?.items) ? list.items : []);
        setLocalPath(list?.base ?? target);
        setSelectedLocal(null);
        setLocalSelectedSet(new Set());
      } catch (err) {
        console.error('refreshLocal failed', err);
      } finally {
        setLoadingLocal(false);
      }
    },
    [api, localPath]
  );

  const refreshRemote = useCallback(
    async (target = remotePath, options = {}) => {
      if (!api) return;
      setLoadingRemote(true);
      setLoadingRemotePath(target || '/');
      try {
        const requestedPage =
          Number.isInteger(options?.page) && options.page > 0 ?
            options.page :
            target === remotePath ?
              Number(remoteEntries?.page) || 1 :
              1;
        const list = await api.listRemote(target || '/', { page: requestedPage });
        setRemoteEntries(normalizeListingResult(list));
        setRemotePath(target || '/');
        setSelectedRemote(null);
        setRemoteSelectedSet(new Set());
        if (addLog) addLog(`Listed ${target || '/'}`);
      } catch (err) {
        console.error('refreshRemote failed', err);
        if (addLog) addLog(`Error listing ${target}: ${err.message}`);
      } finally {
        setLoadingRemote(false);
        setLoadingRemotePath(null);
      }
    },
    [api, remoteEntries?.page, remotePath, addLog]
  );

  const handleCreateLocalFile = async (name) => {
    if (!api) return;
    const safeName = String(name || '').trim();
    if (localEntries.some((entry) => entry.name === safeName)) {
      throw createDuplicateNameError(safeName);
    }
    const target = joinLocalPath(localPath, safeName);
    await api.writeLocalFile(target, '');
    await refreshLocal(localPath);
    if (addLog) addLog(`Created local file ${target}`);
  };

  const handleCreateLocalFolder = async (name) => {
    if (!api) return;
    const safeName = String(name || '').trim();
    if (localEntries.some((entry) => entry.name === safeName)) {
      throw createDuplicateNameError(safeName);
    }
    const target = joinLocalPath(localPath, safeName);
    await api.createLocalDir(target);
    await refreshLocal(localPath);
    if (addLog) addLog(`Created local folder ${target}`);
  };

  const handleCreateRemoteFolder = async (name) => {
    if (!api) return;
    const safeName = String(name || '').trim();
    if (remoteEntries.some((entry) => entry.name === safeName)) {
      throw createDuplicateNameError(safeName);
    }
    const target = joinRemotePath(remotePath, safeName);
    await api.createRemoteDir(target);
    await refreshRemote(remotePath, { force: true });
    if (addLog) addLog(`Created remote folder ${target}`);
  };

  const handleDeleteLocal = async (items) => {
    if (!api) return;
    for (const item of items) {
      await api.deleteLocal(item.path);
      if (addLog) addLog(`Deleted local ${item.path}`);
    }
    await refreshLocal(localPath);
  };

  const handleDeleteRemote = async (items) => {
    if (!api) return;
    for (const item of items) {
      await api.deleteRemote(item.path);
      if (addLog) addLog(`Deleted remote ${item.path}`);
    }
    await refreshRemote(remotePath);
  };

  return {
    localPath,
    setLocalPath,
    remotePath,
    setRemotePath,
    localEntries,
    remoteEntries,
    setRemoteEntries,
    selectedLocal,
    setSelectedLocal,
    selectedRemote,
    setSelectedRemote,
    localSelectedSet,
    setLocalSelectedSet,
    remoteSelectedSet,
    setRemoteSelectedSet,
    loadingRemote,
    loadingRemotePath,
    loadingLocal,
    refreshLocal,
    refreshRemote,
    handleCreateLocalFile,
    handleCreateLocalFolder,
    handleCreateRemoteFolder,
    handleDeleteLocal,
    handleDeleteRemote
  };
};
